/**
 * Minimal markdown renderer for the Word add-in (webpack/CommonJS environment).
 * Handles the subset Gemini typically produces:
 *   headings (#, ##, ###), bold (**), italic (*_), inline code (`),
 *   fenced code blocks (```), unordered lists (- / *), ordered lists (1.),
 *   blockquotes (>), horizontal rules (---).
 */
import * as React from 'react';
import type { PlaybookArchiveRow } from '../../utils/playbookRecordIndex';
import type { PlaybookRecordPreviewData } from './PlaybookRecordClausePreview';
import { RecordCitationLink } from './RecordCitationLink';

/* ── helpers ─────────────────────────────────────────── */

/**
 * Matches a citation phrase introducing one or more archive record numbers,
 * across the formats the model actually produces in English and Hebrew:
 *
 *   Record 5            Records 5, 6, 7
 *   רשומה 5             רשומות 5, 6, 7
 *   [[Record 5]]        [[Record 5, 6, 7]]   ← explicit stable token
 *
 * Group 1 captures the trailing numeric list (e.g. "5", "5, 6, 7"); we then
 * split it and emit one clickable chip per number.
 */
/** No \\b — JS word boundaries do not work with Hebrew letters. */
const PLAYBOOK_RECORD_REF_RE =
  /(?:\[\[\s*(?:Records?|רשומ(?:ה|ות))\s+([\d, ]+?)\s*\]\]|(?:Records?|רשומ(?:ה|ות))\s+(\d+(?:\s*[,،]\s*\d+)*))/gu;

export type AskMarkdownRecordLinkOptions = {
  validRecordNums?: Set<number>;
  onRecordClick?: (recordNum: number) => void;
  getRecord?: (recordNum: number) => PlaybookArchiveRow | undefined;
  loadPreview?: (row: PlaybookArchiveRow) => Promise<PlaybookRecordPreviewData>;
};

function isHebrew(text: string): boolean {
  return /[\u0590-\u05FF]/.test(text);
}

function parseRecordNumbers(match: string, body: string): number[] {
  const nums: number[] = [];
  for (const part of body.split(/[,،\s]+/)) {
    const n = parseInt(part, 10);
    if (Number.isFinite(n)) nums.push(n);
  }
  if (nums.length === 0) {
    const m = match.match(/\d+/g);
    if (m) for (const s of m) {
      const n = parseInt(s, 10);
      if (Number.isFinite(n)) nums.push(n);
    }
  }
  return nums;
}

/** Convert inline markdown; linkify record citations in English/Hebrew & `[[Record N]]` tokens. */
function inlineToReact(
  raw: string,
  key: string | number,
  recordLinks?: AskMarkdownRecordLinkOptions,
): React.ReactNode {
  if (recordLinks?.onRecordClick && recordLinks.validRecordNums?.size) {
    const parts: React.ReactNode[] = [];
    const re = new RegExp(PLAYBOOK_RECORD_REF_RE.source, PLAYBOOK_RECORD_REF_RE.flags);
    let last = 0;
    let m: RegExpExecArray | null;
    let seg = 0;
    while ((m = re.exec(raw)) !== null) {
      if (m.index > last) {
        parts.push(
          <React.Fragment key={`${key}-t${seg++}`}>
            {inlineToReactCore(raw.slice(last, m.index), `${key}-s${seg}`)}
          </React.Fragment>,
        );
      }

      const fullMatch = m[0];
      const numberBody = m[1] ?? m[2] ?? '';
      const nums = parseRecordNumbers(fullMatch, numberBody);
      const valid = nums.filter((n) => recordLinks.validRecordNums!.has(n));

      if (valid.length === 0) {
        parts.push(<React.Fragment key={`${key}-r${seg++}`}>{fullMatch}</React.Fragment>);
      } else {
        const hebrew = isHebrew(fullMatch);
        const label =
          valid.length === 1
            ? hebrew
              ? `רשומה ${valid[0]}`
              : `Record ${valid[0]}`
            : null;
        const renderLink = (n: number, linkLabel: React.ReactNode, linkKey: string) => {
          const row = recordLinks.getRecord?.(n);
          if (row && recordLinks.loadPreview) {
            return (
              <RecordCitationLink
                key={linkKey}
                recordNum={n}
                label={linkLabel}
                row={row}
                loadPreview={recordLinks.loadPreview}
                onOpenSource={recordLinks.onRecordClick!}
              />
            );
          }
          return (
            <button
              key={linkKey}
              type="button"
              className="ask-md-record-link"
              onClick={() => recordLinks.onRecordClick!(n)}
            >
              {linkLabel}
            </button>
          );
        };

        if (label) {
          parts.push(renderLink(valid[0], label, `${key}-r${seg++}`));
        } else {
          const prefix = hebrew ? 'רשומות' : 'Records';
          parts.push(
            <span key={`${key}-r${seg++}`} className="ask-md-record-link-group">
              <span className="ask-md-record-link-prefix">{prefix} </span>
              {valid.map((n, idx) => (
                <React.Fragment key={`${key}-r${seg}-${idx}`}>
                  {renderLink(n, n, `${key}-r${seg}-${idx}`)}
                  {idx < valid.length - 1 ? <span>, </span> : null}
                </React.Fragment>
              ))}
            </span>,
          );
        }
      }

      last = m.index + fullMatch.length;
    }
    if (last < raw.length) {
      parts.push(
        <React.Fragment key={`${key}-t${seg++}`}>
          {inlineToReactCore(raw.slice(last), `${key}-e${seg}`)}
        </React.Fragment>,
      );
    }
    if (parts.length) return <>{parts}</>;
  }

  return inlineToReactCore(raw, key);
}

function inlineToReactCore(raw: string, key: string | number): React.ReactNode {
  const parts: React.ReactNode[] = [];
  // inline code, bold+italic, bold, italic
  const pattern = /(`[^`]+`|\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|__(.+?)__|_(.+?)_|\*(.+?)\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;

  while ((m = pattern.exec(raw)) !== null) {
    if (m.index > last) {
      parts.push(<React.Fragment key={`t${key}-${i++}`}>{raw.slice(last, m.index)}</React.Fragment>);
    }
    const token = m[0];
    if (token.startsWith('`')) {
      parts.push(<code key={`c${key}-${i++}`} className="ask-md-code">{token.slice(1, -1)}</code>);
    } else if (token.startsWith('***')) {
      parts.push(<strong key={`bi${key}-${i++}`}><em>{m[2]}</em></strong>);
    } else if (token.startsWith('**') || token.startsWith('__')) {
      parts.push(<strong key={`b${key}-${i++}`}>{m[3] ?? m[4]}</strong>);
    } else {
      parts.push(<em key={`em${key}-${i++}`}>{m[5] ?? m[6]}</em>);
    }
    last = m.index + token.length;
  }
  if (last < raw.length) {
    parts.push(<React.Fragment key={`t${key}-${i++}`}>{raw.slice(last)}</React.Fragment>);
  }
  return <>{parts}</>;
}

type Block =
  | { type: 'h1' | 'h2' | 'h3'; text: string }
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'blockquote'; text: string }
  | { type: 'hr' }
  | { type: 'code'; lang: string; text: string };

function parseBlocks(md: string): Block[] {
  const lines = md.split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // fenced code block
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // closing ```
      blocks.push({ type: 'code', lang, text: codeLines.join('\n') });
      continue;
    }

    // heading
    if (/^### /.test(line)) { blocks.push({ type: 'h3', text: line.slice(4) }); i++; continue; }
    if (/^## /.test(line))  { blocks.push({ type: 'h2', text: line.slice(3) }); i++; continue; }
    if (/^# /.test(line))   { blocks.push({ type: 'h1', text: line.slice(2) }); i++; continue; }

    // horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) { blocks.push({ type: 'hr' }); i++; continue; }

    // blockquote
    if (/^> /.test(line)) {
      const quoteLines: string[] = [];
      while (i < lines.length && /^> /.test(lines[i])) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      blocks.push({ type: 'blockquote', text: quoteLines.join('\n') });
      continue;
    }

    // unordered list
    if (/^[-*+] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+] /.test(lines[i])) {
        items.push(lines[i].replace(/^[-*+] /, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // ordered list
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // blank line — skip
    if (line.trim() === '') { i++; continue; }

    // paragraph — accumulate non-blank lines
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,3} |```|> |[-*+] |\d+\. )/.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      blocks.push({ type: 'p', text: paraLines.join(' ') });
    }
  }

  return blocks;
}

/* ── component ───────────────────────────────────────── */

interface AskMarkdownProps {
  content: string;
  dir?: 'rtl' | 'ltr';
  recordLinks?: AskMarkdownRecordLinkOptions;
}

export const AskMarkdown: React.FC<AskMarkdownProps> = ({
  content,
  dir = 'ltr',
  recordLinks,
}) => {
  const blocks = React.useMemo(() => parseBlocks(content), [content]);
  const linkOpts = recordLinks;

  return (
    <div className={`ask-markdown-content ${dir}`} dir={dir}>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case 'h1': return <h1 key={idx}>{inlineToReact(block.text, idx, linkOpts)}</h1>;
          case 'h2': return <h2 key={idx}>{inlineToReact(block.text, idx, linkOpts)}</h2>;
          case 'h3': return <h3 key={idx}>{inlineToReact(block.text, idx, linkOpts)}</h3>;
          case 'hr': return <hr key={idx} className="ask-md-hr" />;
          case 'p':  return <p key={idx}>{inlineToReact(block.text, idx, linkOpts)}</p>;
          case 'blockquote':
            return (
              <blockquote key={idx} className="ask-md-blockquote">
                {inlineToReact(block.text, idx, linkOpts)}
              </blockquote>
            );
          case 'ul':
            return (
              <ul key={idx}>
                {block.items.map((item, j) => (
                  <li key={j}>{inlineToReact(item, `${idx}-${j}`, linkOpts)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={idx}>
                {block.items.map((item, j) => (
                  <li key={j}>{inlineToReact(item, `${idx}-${j}`, linkOpts)}</li>
                ))}
              </ol>
            );
          case 'code':
            return (
              <pre key={idx} className="ask-md-pre">
                <code>{block.text}</code>
              </pre>
            );
          default: return null;
        }
      })}
    </div>
  );
};
